import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios'
import { CurrentConvertedCalendars } from 'utils/google_events'
import { ThirdPartyAccountByEmail } from './db/Queries/ThirdPartyCalendarsQuieries'
import { connectThirdPartyToServer } from './api/group'
import Database from './db/Database'
import Logger from './Logger'

class GoogleApi {
  instance = axios.create({
    baseURL: `https://www.googleapis.com/calendar/v3/calendars/`
  })

  constructor() {
    // Updated request interceptor for Axios 1.x
    this.instance.interceptors.request.use(
      async (req: InternalAxiosRequestConfig) => {
        const calendarId = req.url?.split('/')[0] ?? ''
        const email = CurrentConvertedCalendars.find(
          (cal) => cal.id === calendarId
        )?.primaryCalendarEmail

        if (email) {
          const account = await ThirdPartyAccountByEmail(email)
          req.headers.set('Authorization', `Bearer ${account.token}`)
        }

        return req
      }
    )

    // Updated response interceptor for Axios 1.x
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        if (!error.response || error.response.status !== 401) {
          return Promise.reject(error)
        }

        // Handle 401 Unauthorized error (token expired)
        const originalRequest = error.config
        if (!originalRequest) {
          return Promise.reject(error)
        }

        const calendarId = originalRequest.url?.split('/')[0] ?? ''
        const email = CurrentConvertedCalendars.find(
          (cal) => cal.id === calendarId
        )?.primaryCalendarEmail

        if (email) {
          try {
            const tokenResponse = await connectThirdPartyToServer({
              email: email,
              action: 'REFRESH_TOKEN',
              type: 'Google'
            })

            Logger.blue('refresh token from google response', tokenResponse)

            const updatedAccount = await Database.write(async () => {
              return (await ThirdPartyAccountByEmail(email)).update((local) => {
                local.token = tokenResponse.access_token
                local._raw['tokenExpire'] = tokenResponse.expiry_date
              })
            })

            // Set the new Authorization header
            originalRequest.headers.Authorization = `Bearer ${updatedAccount.token}`

            // Retry the original request with the new token
            return axios(originalRequest)
          } catch (e) {
            return Promise.reject(e)
          }
        }

        // If we can't refresh the token, reject the promise
        return Promise.reject(error)
      }
    )
  }
}

export default new GoogleApi()
